// src/pages/nations/NationManagement.tsx

/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import NationTable from "@/pages/nations/NationTable";
import NationForm from "@/pages/nations/NationForm";
import NationView from "@/pages/nations/NationView";
import EliminateDialog from "@/pages/nations/EliminateDialog";
import {
  useNationsAdmin,
  useCreateNation,
  useUpdateNation,
  useDeleteNation,
  useEliminateNation,
} from "@/hooks/api/useNations";
import type {
  Nation,
  CreateNationRequest,
  UpdateNationRequest,
  EliminateNationRequest,
} from "@/types/api.types";
import type { NationFilters } from "@/api/services/nations.service";

export default function NationManagement() {
  // ─── Queries & Mutations ─────────────────────────────
  const [filters, setFilters] = useState<NationFilters>({ page: 1, limit: 20 });
  const { data, isLoading, error } = useNationsAdmin(filters);
  const { mutate: createNation, isPending: isCreating } = useCreateNation();
  const { mutate: updateNation, isPending: isUpdating } = useUpdateNation();
  const { mutate: deleteNation } = useDeleteNation();
  const { mutate: eliminateNation, isPending: isEliminating } =
    useEliminateNation();

  // ─── State ──────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [eliminateOpen, setEliminateOpen] = useState(false);
  const [currentNation, setCurrentNation] = useState<Partial<Nation> | null>(
    null,
  );
  const [isEditing, setIsEditing] = useState(false);

  // ─── Handlers ───────────────────────────────────────

  const handleAddNation = () => {
    setIsEditing(false);
    setCurrentNation({
      name: "",
      code: "",
      flagUrl: "",
      groupName: "",
    });
    setIsOpen(true);
  };

  const handleEditNation = (nation: Nation) => {
    setIsEditing(true);
    setCurrentNation(nation);
    setIsOpen(true);
  };

  const handleViewNation = (nation: Nation) => {
    setCurrentNation(nation);
    setViewOpen(true);
  };

  const handleDeleteNation = (id: number) => {
    if (
      !confirm(
        "Are you sure you want to delete this nation? This will also delete all associated players.",
      )
    )
      return;

    deleteNation(id, {
      onSuccess: () => {
        toast.success("Nation deleted successfully");
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || "Failed to delete nation");
      },
    });
  };

  const handleEliminateNation = (nation: Nation) => {
    if (nation.isEliminated) {
      toast.warning("This nation is already eliminated");
      return;
    }
    setCurrentNation(nation);
    setEliminateOpen(true);
  };

  const handleConfirmEliminate = (data: EliminateNationRequest) => {
    if (!currentNation?.id) return;

    eliminateNation(
      { id: currentNation.id, data },
      {
        onSuccess: () => {
          toast.success(
            `${currentNation.name} eliminated. All players deactivated.`,
          );
          setEliminateOpen(false);
          setCurrentNation(null);
        },
        onError: (error: any) => {
          toast.error(
            error.response?.data?.message || "Failed to eliminate nation",
          );
        },
      },
    );
  };

  const handleSaveNation = (
    formData: CreateNationRequest | UpdateNationRequest,
  ) => {
    if (isEditing && currentNation?.id) {
      // Update existing nation
      updateNation(
        { id: currentNation.id, data: formData as UpdateNationRequest },
        {
          onSuccess: () => {
            toast.success("Nation updated successfully");
            setIsOpen(false);
            setCurrentNation(null);
          },
          onError: (error: any) => {
            toast.error(
              error.response?.data?.message || "Failed to update nation",
            );
          },
        },
      );
    } else {
      // Create new nation
      createNation(formData as CreateNationRequest, {
        onSuccess: () => {
          toast.success("Nation created successfully");
          setIsOpen(false);
          setCurrentNation(null);
        },
        onError: (error: any) => {
          toast.error(
            error.response?.data?.message || "Failed to create nation",
          );
        },
      });
    }
  };

  // ─── Render ─────────────────────────────────────────

  if (error) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-red-600 mb-4">Failed to load nations</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Nation Management
          </h1>
          <p className="text-gray-500 mt-1">
            Manage World Cup participating nations
          </p>
        </div>
        <Button
          onClick={handleAddNation}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          disabled={isCreating}
        >
          <Plus className="mr-2 h-4 w-4" /> Add Nation
        </Button>
      </div>

      <NationTable
        nations={data?.nations || []}
        isLoading={isLoading}
        filters={filters}
        onFiltersChange={(f) => setFilters({ ...f, page: 1 })}
        onClearFilters={() => setFilters({ page: 1, limit: 20 })}
        onEdit={handleEditNation}
        onView={handleViewNation}
        onDelete={handleDeleteNation}
        onEliminate={handleEliminateNation}
        pagination={data?.pagination}
        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
        onPageSizeChange={(limit) => setFilters((prev) => ({ ...prev, limit, page: 1 }))}
      />

      <NationForm
        open={isOpen}
        onOpenChange={setIsOpen}
        nation={currentNation}
        isEditing={isEditing}
        onSave={handleSaveNation}
        isSaving={isCreating || isUpdating}
      />

      <NationView
        open={viewOpen}
        onOpenChange={setViewOpen}
        nation={currentNation as Nation}
      />

      <EliminateDialog
        open={eliminateOpen}
        onOpenChange={setEliminateOpen}
        nation={currentNation as Nation}
        onConfirm={handleConfirmEliminate}
        isEliminating={isEliminating}
      />
    </Layout>
  );
}
